import React, { useRef, useEffect, useState } from 'react';
import { Stage, Layer, Line, Text, Transformer } from 'react-konva';
import useLayersStore from '../stores/layersStore';

// Create a new context for the stage ref
export const StageContext = React.createContext(null);

const DrawingCanvas = ({ children }) => {
  const stageRef = useRef(null);
  const isDrawing = useRef(false);
  const { currentTool, currentColor, brushSize } = useLayersStore();
  const [lines, setLines] = React.useState([]);
  const [texts, setTexts] = useState([]);
  const [selectedId, selectShape] = useState(null);
  const [textEditVisible, setTextEditVisible] = useState(false);
  const transformerRef = useRef();

  // Add this effect to update transformer nodes when selection changes
  useEffect(() => {
    if (selectedId) {
      const node = stageRef.current.findOne(`#${selectedId}`);
      if (transformerRef.current && node) {
        // Add delete button to transformer
        const deleteButton = new Konva.Circle({
          radius: 10,
          opacity: 0,
        });

        const cross = new Konva.Path({
          data: 'm194.800781 164.769531 128.210938-128.214843c8.34375-8.339844 8.34375-21.824219 0-30.164063-8.339844-8.339844-21.824219-8.339844-30.164063 0l-128.214844 128.214844-128.210937-128.214844c-8.34375-8.339844-21.824219-8.339844-30.164063 0-8.34375 8.339844-8.34375 21.824219 0 30.164063l128.210938 128.214843-128.210938 128.214844c-8.34375 8.339844-8.34375 21.824219 0 30.164063 4.15625 4.160156 9.621094 6.25 15.082032 6.25 5.460937 0 10.921875-2.089844 15.082031-6.25l128.210937-128.214844 128.214844 128.214844c4.160156 4.160156 9.621094 6.25 15.082032 6.25 5.460937 0 10.921874-2.089844 15.082031-6.25 8.34375-8.339844 8.34375-21.824219 0-30.164063zm0 0',
          fill: 'red',
          scale: {
            x: 0.04,
            y: 0.04,
          },
        });

        transformerRef.current.add(cross);
        transformerRef.current.add(deleteButton);

        // Position the delete button
        const updatePos = () => {
          const pos = transformerRef.current.findOne('.top-right').position();
          pos.y -= 15;
          cross.position(pos);
          pos.y += 5;
          pos.x += 5;
          deleteButton.position(pos);
        };

        updatePos();

        // Add click handler to delete button
        deleteButton.on('click', () => {
          setTexts(texts.filter(t => t.id !== selectedId));
          selectShape(null);
        });

        // Add hover effects
        deleteButton.on('mouseenter', () => {
          const stage = stageRef.current;
          if (stage) {
            stage.container().style.cursor = 'pointer';
          }
        });

        deleteButton.on('mouseleave', () => {
          const stage = stageRef.current;
          if (stage) {
            stage.container().style.cursor = '';
          }
        });

        // Update position when text is transformed
        node.on('transform', updatePos);

        transformerRef.current.nodes([node]);
        transformerRef.current.getLayer().batchDraw();
      }
    }
  }, [selectedId, texts]);

  const handleMouseDown = e => {
    if (currentTool === 'text') return;

    isDrawing.current = true;
    const pos = e.target.getStage().getPointerPosition();
    setLines([
      ...lines,
      {
        tool: currentTool,
        points: [pos.x, pos.y],
        color: currentTool === 'eraser' ? '#ffffff' : currentColor,
        strokeWidth: brushSize,
      },
    ]);
  };

  const handleMouseMove = e => {
    if (!isDrawing.current) return;

    const stage = e.target.getStage();
    const point = stage.getPointerPosition();
    const lastLine = lines[lines.length - 1];

    // Add point to last line
    lastLine.points = lastLine.points.concat([point.x, point.y]);

    // Replace last line with updated line
    setLines([...lines.slice(0, -1), lastLine]);
  };

  const handleMouseUp = () => {
    isDrawing.current = false;
  };

  const handleTextDblClick = e => {
    const id = e.target.id();
    const textNode = e.target;

    // Hide the text node immediately
    textNode.hide();
    textNode.getLayer().batchDraw();

    // Create textarea
    const textPosition = textNode.absolutePosition();
    const textarea = document.createElement('textarea');
    document.body.appendChild(textarea);

    textarea.value = textNode.text();
    textarea.style.position = 'absolute';
    textarea.style.top = `${textPosition.y}px`;
    textarea.style.left = `${textPosition.x}px`;
    textarea.style.width = '200px';
    textarea.style.fontSize = '20px';
    textarea.style.border = 'none';
    textarea.style.padding = '0px';
    textarea.style.margin = '0px';
    textarea.style.overflow = 'hidden';
    textarea.style.background = 'transparent';
    textarea.style.outline = 'none';
    textarea.style.resize = 'none';
    textarea.style.zIndex = '1000';
    textarea.style.color = currentColor;
    textarea.style.lineHeight = '1';
    textarea.style.minHeight = '0';
    textarea.style.boxShadow = 'none';
    textarea.style.webkitAppearance = 'none';
    textarea.style.backgroundColor = 'transparent';
    textarea.style.caretColor = currentColor;

    textarea.focus();

    textarea.addEventListener('blur', () => {
      setTexts(texts.map(t => (t.id === id ? { ...t, text: textarea.value } : t)));
      document.body.removeChild(textarea);
      textNode.show();
      textNode.getLayer().batchDraw();
      setTextEditVisible(false);
    });
  };

  const handleStageClick = e => {
    const clickedOnEmpty = e.target === e.target.getStage();
    const clickedOnText = e.target.getType() === 'Text';
    const clickedOnTransformer = e.target.getType() === 'Transformer';

    if (clickedOnEmpty) {
      selectShape(null);

      if (currentTool === 'text') {
        const pos = e.target.getStage().getPointerPosition();
        const newTextId = `text-${Date.now()}`;

        setTexts(prev => [
          ...prev,
          {
            id: newTextId,
            x: pos.x,
            y: pos.y,
            text: '', // Start with empty text
            fontSize: 20,
            fill: currentColor,
          },
        ]);

        // Wait for the next render cycle before creating textarea
        setTimeout(() => {
          const textNode = stageRef.current.findOne(`#${newTextId}`);
          if (textNode) {
            textNode.hide();
            textNode.getLayer().batchDraw();

            const textPosition = textNode.absolutePosition();
            const textarea = document.createElement('textarea');
            document.body.appendChild(textarea);

            // Apply same styles as above
            textarea.style.position = 'absolute';
            textarea.style.top = `${textPosition.y}px`;
            textarea.style.left = `${textPosition.x}px`;
            textarea.style.width = '200px';
            textarea.style.fontSize = '20px';
            textarea.style.border = 'none';
            textarea.style.padding = '0px';
            textarea.style.margin = '0px';
            textarea.style.overflow = 'hidden';
            textarea.style.background = 'transparent';
            textarea.style.outline = 'none';
            textarea.style.resize = 'none';
            textarea.style.zIndex = '1000';
            textarea.style.color = currentColor;
            textarea.style.lineHeight = '1';
            textarea.style.minHeight = '0';
            textarea.style.boxShadow = 'none';
            textarea.style.webkitAppearance = 'none';
            textarea.style.backgroundColor = 'transparent';
            textarea.style.caretColor = currentColor;

            textarea.focus();

            textarea.addEventListener('blur', () => {
              const newText = textarea.value || 'Double click to edit';
              setTexts(prev => prev.map(t => (t.id === newTextId ? { ...t, text: newText } : t)));
              document.body.removeChild(textarea);
              textNode.show();
              textNode.getLayer().batchDraw();
              setTextEditVisible(false);
            });
          }
        }, 0);
      }
      return;
    }

    if (clickedOnText || clickedOnTransformer) {
      return;
    }
  };

  return (
    <StageContext.Provider value={stageRef}>
      <Stage
        width={window.innerWidth}
        height={window.innerHeight}
        onMouseDown={handleMouseDown}
        onMousemove={handleMouseMove}
        onMouseup={handleMouseUp}
        ref={stageRef}
        className="fixed top-0 left-0 w-full h-full z-40"
        onClick={handleStageClick}
      >
        <Layer>
          {lines.map((line, i) => (
            <Line
              key={i}
              points={line.points}
              stroke={line.color}
              strokeWidth={line.strokeWidth}
              tension={0.5}
              lineCap="round"
              lineJoin="round"
              globalCompositeOperation={line.tool === 'eraser' ? 'destination-out' : 'source-over'}
            />
          ))}

          {texts.map(text => (
            <Text
              key={text.id}
              id={text.id}
              {...text}
              draggable
              onDblClick={handleTextDblClick}
              onClick={e => {
                e.cancelBubble = true;
                selectShape(text.id);
              }}
            />
          ))}

          {selectedId && (
            <Transformer
              ref={transformerRef}
              boundBoxFunc={(oldBox, newBox) => {
                // Limit resize
                newBox.width = Math.max(30, newBox.width);
                return newBox;
              }}
            />
          )}
        </Layer>
      </Stage>
      {children}
    </StageContext.Provider>
  );
};

export default DrawingCanvas;
